import { get } from "lodash-es";

import {
  GLOBAL_SHARED_ACCESS_ID,
  LINKED_SECTION_SEEDING_ACCESS_ID,
  groupAccessIdFromGroupId,
  studentAccessIdFromUserId,
} from "../shared/access-id";
import { EnhancedOp } from "../shared/types/sharedb";
import {
  AUTH_ERRORS,
  PagePermissionType,
  WebsocketUserDataType,
} from "../shared/types/websocket-auth";
import { AccessIdMetaDataType, WorksheetType } from "../shared/types/worksheet";
import {
  ACCESS_ID_DATA_META_FIELDS,
  BLOCKS_PATH,
  DATA_PATH,
  VERSIONS_PATH,
} from "../shared/worksheets/constants";

import {
  getComputedRole,
  getOrgCapabilities,
  getPageCapabilitiesForServer,
  getSectionCapabilities,
} from "./capabilities";

/**
 * This is a helpers function to figure out the specific intention / meaning of
 * an operation performed on a sharedb document. We use this to determine whether
 * the operation is executing allowed actions or not.
 */
export const inspectOp = (op: EnhancedOp) => {
  const path = op.p;
  const isDataOp = op.p[0] === DATA_PATH;
  // Check for both "oi" (object insert) and "oid" (object insert default) operations
  const hasObjectInsert = "oi" in op || "oid" in op;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const insertValue = "oi" in op ? op.oi : "oid" in op ? op.oid : undefined;
  // Our worksheet data structure defines subpaths for specific user/groups data, and in each,
  // subpaths for different fields. These subpaths need to be initialized by the client before
  // writing to them initially. We call these structures "accessIdWrapper" and "fieldDataWrapper".
  // Operations to initialize them are generally allowed, even if you don't have write permissions
  // otherwise, to simplify the client's logic, but in turn, we have to rigorously check that
  // operations writing to these paths are well-formed, so as to not overwrite existing data or
  // create malformed data.
  const isInitializingAccessIdWrapper = isDataOp && path.length === 2;
  const isInitializingFieldWrapper =
    isDataOp &&
    hasObjectInsert &&
    path.length === 3 &&
    !ACCESS_ID_DATA_META_FIELDS.includes(path[2] as keyof AccessIdMetaDataType);
  const isWrapperOp = hasObjectInsert && typeof insertValue === "object" && insertValue !== null;
  const isAccessIdWrapperOp =
    isWrapperOp &&
    Object.keys(insertValue as object).length === 1 &&
    VERSIONS_PATH in (insertValue as object);
  // Every field wrapper is initialized with different kinds of data, so it's hard to make any specific
  // checks. For now, we just check for whether there is a wrapper object, and don't check the content.
  const isFieldDataWrapperOp = isWrapperOp;
  // "oid" operations are "object insert default" - they only insert if path doesn't exist,
  // so they should never be considered as overwriting even if data exists at the path.
  const isDefaultInsertOp = "oid" in op;

  const checks = {
    isDataOp,
    isTemplateOp: op.p[0] === BLOCKS_PATH,
    isRootOp: op.p.length === 1,
    isAllowedAccessIdOp: (isStaffUser: boolean, allowedAccessIds: string[]) =>
      path.length >= 2 && (isStaffUser || allowedAccessIds.includes(String(path[1]))),
    isInitializingAccessIdWrapper,
    isInitializingFieldWrapper,
    isAccessIdWrapperOp,
    isFieldDataWrapperOp,
    isWrapperInitOp: isInitializingAccessIdWrapper || isInitializingFieldWrapper,
    isDefaultInsertOp,
  };

  return checks;
};

export class AuthError extends Error {
  constructor(public code: keyof typeof AUTH_ERRORS) {
    super(`Auth error: ${code}`);
  }
}

export const getPageByDocId = (
  docId: string | null,
  pages: PagePermissionType[],
): PagePermissionType | null => {
  return docId ? pages.find((page) => page.worksheet_id === docId) || null : null;
};

export const getCapabilitiesFromUserData = (
  docId: string | null,
  userData: WebsocketUserDataType,
) => {
  const {
    pages,
    section_role: sectionRole,
    course_role: courseRole,
    org_role: orgRole,
    user_role: userRole,
    is_superuser: isSuperuser,
  } = userData;

  const userOrgRole = getComputedRole({ isSuperuser, userRole, orgRole });
  const orgCapabilities = getOrgCapabilities(userOrgRole);

  const computedRole = getComputedRole({ isSuperuser, userRole, sectionRole, orgRole, courseRole });
  const sectionCapabilities = getSectionCapabilities(computedRole);

  const page = getPageByDocId(docId, pages);
  const pageCapabilities = getPageCapabilitiesForServer(page, computedRole);
  const pageGroupIds = page?.page_group_id ? [page.page_group_id] : [];
  return { ...sectionCapabilities, ...orgCapabilities, ...pageCapabilities, pageGroupIds };
};

/**
 * Returns a list of all access IDs the user is allowed to read/write in a document _specifically_.
 * This does not list implicit access IDs, like an admin having access to all documents, or a student
 * being able to see all other students' data after a rollup. Those cases are covered by
 * `userCanSeeOtherUsersDataAndPresence`. Note that only users with canAuthorSection should be able to
 * write to LINKED_SECTION_SEEDING_ACCESS_ID.
 */
export const getAllowedAccessIds = (
  userId: string | null,
  canViewPage: boolean,
  pageGroupIds: string[],
) => {
  if (!canViewPage || !userId) {
    return [];
  }
  return [
    GLOBAL_SHARED_ACCESS_ID,
    LINKED_SECTION_SEEDING_ACCESS_ID,
    studentAccessIdFromUserId(userId),
    ...pageGroupIds.map(groupAccessIdFromGroupId),
  ];
};

/**
 * Checks a write operation against the user's permissions.
 * Returns nothing if successful, or an error object detailing why the operation is not allowed.
 */
export const checkUserCanPerformOperation = (
  userId: string | null,
  canAuthorSection: boolean,
  canViewPage: boolean,
  pageGroupIds: string[],
  op: EnhancedOp,
  docData: WorksheetType,
  // If we try to overwrite on the frontend we'll allow it to propogate to the backend as
  // some methods have special handling for the overwrite case.
  canOverwrite: boolean = false,
) => {
  const path = op.p;
  const canWriteToPage = canViewPage || canAuthorSection;
  const userOp = inspectOp(op);
  const isOverWritingData = Boolean(get(docData, path));
  const allowedAccessIds = canAuthorSection
    ? []
    : getAllowedAccessIds(userId, canViewPage, pageGroupIds);
  const isAllowedAccessIdOp = userOp.isAllowedAccessIdOp(canAuthorSection, allowedAccessIds);

  // Read-only worksheets are never editable
  if (userOp.isRootOp) {
    // Can't replace any root elements except for "locked" status, and only for staff
    if (path[0] !== "locked" || !canAuthorSection) {
      throw new AuthError(AUTH_ERRORS.DOC_GLOBAL_EDIT_DISALLOWED);
    }
  } else if (userOp.isTemplateOp) {
    // Template data operations are only allowed for staff
    if (!canAuthorSection) {
      throw new AuthError(AUTH_ERRORS.MISSING_EDIT_PERMISSION);
    }
  } else if (docData?.locked && !userOp.isWrapperInitOp) {
    // No operations are allowed on locked documents, except wrapper initialization
    throw new AuthError(AUTH_ERRORS.DOC_READ_ONLY);
  } else if (userOp.isDataOp) {
    // The seeding accessId is read-only for non-staff users. This must apply even to wrapper init ops,
    // because wrapper-init would otherwise allow students to populate `d.s.*`.
    if (
      typeof path[1] === "string" &&
      path[1] === LINKED_SECTION_SEEDING_ACCESS_ID &&
      !canAuthorSection
    ) {
      throw new AuthError(AUTH_ERRORS.MISSING_WRITE_PERMISSION_FOR_PATH);
    }

    // User data operations are only allowed for staff or users with explicit permissions
    if (userOp.isWrapperInitOp) {
      // It's fine to initialize a new access id wrapper (for anyone, with any permissions)
      // or a field data wrapper, even if the document is read-only and for other access IDs,
      // but you can't replace or delete existing wrappers.
      // "oid" (object insert default) ops skip the overwrite check - they're designed to
      // only insert if the path doesn't exist, so the server will handle the race condition.
      if (isOverWritingData && !canOverwrite && !userOp.isDefaultInsertOp) {
        throw new AuthError(AUTH_ERRORS.OVERWRITE_DISALLOWED);
      }
      // If you are initializing an accessId wrapper, it should have the right data in it
      if (userOp.isInitializingAccessIdWrapper && !userOp.isAccessIdWrapperOp) {
        throw new AuthError(AUTH_ERRORS.ACCESS_ID_WRAPPER_MALFORMED);
      }
      if (userOp.isInitializingFieldWrapper && !userOp.isFieldDataWrapperOp) {
        throw new AuthError(AUTH_ERRORS.FIELD_DATA_WRAPPER_MALFORMED);
      }
    } else if (typeof path[1] !== "string") {
      throw new AuthError(AUTH_ERRORS.EDIT_MALFORMED);
    } else if (!canWriteToPage) {
      throw new AuthError(AUTH_ERRORS.MISSING_WRITE_PERMISSION);
    } else if (!isAllowedAccessIdOp) {
      // You can only write to your own access id data, unless you're an admin
      throw new AuthError(AUTH_ERRORS.MISSING_WRITE_PERMISSION_FOR_PATH);
    }
  } else {
    // All operations not strictly allowed are disallowed
    throw new AuthError(AUTH_ERRORS.DOC_GLOBAL_EDIT_DISALLOWED);
  }
};
